/**
 * Ficha de producto (CAT-03).
 *
 * El `<h1>` es el nombre exacto del producto, tal como esta en el catalogo: es
 * lo que la gente escribe en Google y lo que tiene que calzar con el `name` del
 * dato estructurado. Debajo, un `<article>` con la foto y los datos, y una
 * seccion aparte con lo relacionado.
 *
 * La ficha cierra de dos maneras y las dos importan: al carro (CHK-01) y por
 * WhatsApp con el producto y el codigo ya escritos (WSP-01), que es como se
 * vende hoy. Un producto agotado solo ofrece la segunda.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { Producto, ProductoResumen } from '@elcarpintero/compartido';

import { BotonAgregar } from '../componentes/carro/BotonAgregar';
import { SelectorCantidad } from '../componentes/carro/SelectorCantidad';
import { BloqueDespacho } from '../componentes/ficha/BloqueDespacho';
import { BloqueInfo } from '../componentes/ficha/BloqueInfo';
import { CajaStock } from '../componentes/ficha/CajaStock';
import { DetallePaquete } from '../componentes/ficha/DetallePaquete';
import { GaleriaProducto } from '../componentes/ficha/GaleriaProducto';
import { TablaEspecificaciones } from '../componentes/ficha/TablaEspecificaciones';
import { GrillaProductos } from '../componentes/catalogo/GrillaProductos';
import { AvisoError, Cargando } from '../componentes/comunes/EstadoPeticion';
import { Icono } from '../componentes/comunes/Icono';
import { Precio } from '../componentes/comunes/Precio';
import { Migas } from '../componentes/diseno/Migas';
import { registrarVistaProducto } from '../medicion/eventos';
import { useDespacho } from '../contexto/despacho-contexto';
import { umbralDespachoGratis } from '../utilidades/entrega';
import { leerFichaTecnica } from '../utilidades/ficha-tecnica';
import { usePeticion } from '../hooks/use-peticion';
import { DatosEstructurados } from '../seo/DatosEstructurados';
import { esquemaMigas, esquemaProducto, migasDeCategoria } from '../seo/esquemas';
import { Seo } from '../seo/Seo';
import { urlAbsoluta } from '../seo/urls';
import { MAXIMO_POR_LINEA } from '../utilidades/carro';
import { obtenerFichaProducto } from '../servicios/catalogo.servicio';
import { registrarClicWhatsapp } from '../servicios/metricas.servicio';
import { pesos, porcentajeDescuento } from '../utilidades/formato';
import { esPaquete, textoDisponibles, textoPiezas } from '../utilidades/paquete';
import { rutas } from '../utilidades/rutas';
import { whatsappProducto } from '../utilidades/whatsapp';

/**
 * El carro guarda el resumen del producto, no la ficha entera: es lo que la
 * linea del carro necesita para dibujarse y lo que devuelve la conciliacion al
 * volver a la tienda (CHK-02). La foto principal es la primera de la galeria.
 */
function resumenDe(producto: Producto): ProductoResumen {
  return {
    id: producto.id,
    sku: producto.sku,
    nombre: producto.nombre,
    slug: producto.slug,
    marca: producto.marca,
    precio: producto.precio,
    precioAnterior: producto.precioAnterior,
    unidadesPorPaquete: producto.unidadesPorPaquete,
    unidadMedida: producto.unidadMedida,
    estadoStock: producto.estadoStock,
    stock: producto.stock,
    imagen: producto.imagenes[0] ?? null,
  };
}

export function FichaProducto() {
  const { slug = '' } = useParams();
  const [cantidad, setCantidad] = useState(1);
  // Pasar a un relacionado no desmonta la vista: sin esto la cantidad elegida
  // en el producto anterior seguia puesta en el nuevo.
  const [slugDeCantidad, setSlugDeCantidad] = useState(slug);
  if (slug !== slugDeCantidad) {
    setSlugDeCantidad(slug);
    setCantidad(1);
  }
  const { opciones: opcionesDespacho } = useDespacho();
  // Sin despacho, o sin despacho gratis, no se anuncia bajo el precio.
  const despachoGratisDesde = umbralDespachoGratis(opcionesDespacho);
  const { estado, datos, error } = usePeticion(
    (senal) => obtenerFichaProducto(slug, senal),
    [slug],
  );

  // Una vez por ficha cargada: pasar a un relacionado trae datos nuevos.
  useEffect(() => {
    if (datos !== null) registrarVistaProducto(resumenDe(datos.producto));
  }, [datos]);

  if (estado === 'cargando') {
    return (
      <div className="contenedor pagina">
        <Cargando que="el producto" pagina />
      </div>
    );
  }

  if (datos === null) {
    return (
      <div className="contenedor pagina">
        <Seo
          titulo="Producto no encontrado"
          descripcion="El producto que buscas no está disponible en el catálogo."
          ruta={rutas.producto(slug)}
          sinIndexar
        />
        <AvisoError error={error} />
      </div>
    );
  }

  const { producto, categoria, linea, relacionados } = datos;
  const descuento = porcentajeDescuento(producto.precio, producto.precioAnterior);
  const agotado = producto.estadoStock === 'agotado';
  const ficha = leerFichaTecnica(producto.fichaTecnica);
  const migas = [
    ...migasDeCategoria(linea, categoria),
    { nombre: producto.nombre },
  ];
  const url = urlAbsoluta(rutas.producto(producto.slug));
  const foto = producto.imagenes[0];

  return (
    <>
      <Seo
        titulo={producto.tituloSeo ?? producto.nombre}
        descripcion={producto.descripcionSeo ?? producto.descripcion ?? producto.nombre}
        ruta={rutas.producto(producto.slug)}
        tipo="product"
        imagen={foto?.url}
      />
      <DatosEstructurados esquemas={[esquemaProducto(datos), esquemaMigas(migas)]} />

      <div className="contenedor pagina">
        <Migas migas={migas} />

        <article className="ficha">
          <GaleriaProducto
            imagenes={producto.imagenes}
            nombre={producto.nombre}
            etiquetas={
              <>
                {descuento !== null ? (
                  <span className="etq etq-oferta">-{descuento}% de descuento</span>
                ) : null}
                {agotado ? <span className="etq etq-critico">Sin stock</span> : null}
              </>
            }
          />

          <div className="ficha-datos">
            <p className="ficha-linea-meta">
              {producto.marca !== null ? (
                <span className="etq etq-neutra">{producto.marca}</span>
              ) : null}
              <span className="mono">{producto.sku}</span>
            </p>

            <h1>{producto.nombre}</h1>

            {producto.descripcion !== null ? (
              <p className="ficha-lead">{producto.descripcion}</p>
            ) : null}

            <section className="ficha-compra-card" aria-label="Comprar este producto">
              <Precio
                precio={producto.precio}
                precioAnterior={producto.precioAnterior}
                tamano="grande"
              />
              <p className="ficha-iva">
                {esPaquete(producto)
                  ? `Precio del paquete de ${textoPiezas(producto, 1)}, con IVA incluido`
                  : 'Precio con IVA incluido'}
                {despachoGratisDesde !== null
                  ? producto.precio >= despachoGratisDesde
                    ? ' · Despacho gratis a todo Chile'
                    : ` · Despacho gratis sobre ${pesos(despachoGratisDesde)}`
                  : ''}
              </p>

              <DetallePaquete producto={producto} />

              <CajaStock producto={producto} />

              <div className="ficha-ctas">
                {agotado ? null : (
                  <div className="ficha-cantidad">
                    <SelectorCantidad
                      cantidad={cantidad}
                      nombre={producto.nombre}
                      enPaquetes={esPaquete(producto)}
                      minimo={1}
                      alCambiar={(valor) => setCantidad(Math.min(Math.max(valor, 1), MAXIMO_POR_LINEA))}
                    />
                    <BotonAgregar
                      producto={resumenDe(producto)}
                      cantidad={cantidad}
                      tamano="lg"
                      bloque
                    />
                    {esPaquete(producto) ? (
                      <p className="ficha-total-piezas" aria-live="polite">
                        {textoDisponibles(producto, cantidad)} = {textoPiezas(producto, cantidad)}
                      </p>
                    ) : null}
                  </div>
                )}

                <a
                  className={agotado ? 'btn btn-lg btn-wa btn-bloque' : 'btn btn-wa-suave btn-bloque'}
                  href={whatsappProducto(producto.nombre, producto.sku, url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    registrarClicWhatsapp({ origen: 'ficha', productoSku: producto.sku })
                  }
                >
                  <Icono nombre="wa" />
                  {agotado ? 'Consultar cuándo llega' : 'Comprar por WhatsApp'}
                </a>
              </div>
            </section>

            <BloqueInfo icono="camion" titulo="Entrega">
              <BloqueDespacho />
            </BloqueInfo>

            <BloqueInfo icono="lista" titulo="Ficha técnica">
              <TablaEspecificaciones
                especificaciones={[
                  { campo: 'Código', valor: producto.sku },
                  ...(producto.marca !== null ? [{ campo: 'Marca', valor: producto.marca }] : []),
                  ...ficha.especificaciones,
                  { campo: 'Categoría', valor: categoria.nombre },
                  { campo: 'Línea', valor: linea.nombre },
                  { campo: 'Se vende por', valor: producto.unidadMedida },
                  {
                    campo: 'Disponibilidad',
                    valor: agotado ? 'Sin stock' : `${producto.stock} unidades`,
                  },
                  { campo: 'Documento', valor: 'Boleta o factura electrónica' },
                ]}
              />
              {ficha.notas.length > 0 ? (
                <ul className="ficha-notas">
                  {ficha.notas.map((nota) => (
                    <li key={nota}>{nota}</li>
                  ))}
                </ul>
              ) : null}
            </BloqueInfo>
          </div>
        </article>

        {relacionados.length > 0 ? (
          <section className="seccion seccion--relacionados" aria-labelledby="relacionados">
            <div className="seccion-cab">
              <div>
                <h2 id="relacionados">Se compra junto con</h2>
                <p className="sub">Del mismo rubro y de la misma marca</p>
              </div>
            </div>
            <GrillaProductos productos={relacionados} titulo="Productos relacionados" />
          </section>
        ) : null}
      </div>
    </>
  );
}
